<script setup lang="ts">
import { useDisplay } from 'vuetify'

const { smAndDown } = useDisplay()
const id = extractId(useRoute().params.id as string)
const { $api } = useNuxtApp()
const currentYear = new Date().getFullYear()

const { data, error } = await useAsyncData(`profile-${id}`, async () => {
  const [profile, entries, activities, leaderboard, raceStats, skipperRanking] = await Promise.all([
    $api(`users/${id}/`),
    $api(`competitions/entries/?user=${id}&ordering=competition_class__competition__start_date`),
    $api(`performance/activities/?user=${id}&ordering=-scoring_start&page_size=4`),
    $api(`performance/challenge-leaderboard/?year=${currentYear}`).catch(() => null),
    $api(`performance/activities/?user=${id}&scoring_speed_corrected_ms__isnull=false&ordering=-scoring_speed_corrected_ms&page_size=1`).catch(() => null),
    $api(`performance/skipper/?year=${currentYear}&user=${id}`).catch(() => null),
  ])
  return { profile, entries, activities, leaderboard, raceStats, skipperRanking }
})

if (error.value) {
  throw createError({ statusCode: error.value.statusCode ?? 404, statusMessage: `Page not found: ${useRoute().fullPath}` })
}

const profile = data.value?.profile
const entries = ref(data.value?.entries.results)
const totalItems = ref(data.value?.entries.count as number)
const activities = data.value?.activities.results ?? []

const raceCount = data.value?.raceStats?.count ?? 0
const bestRaceSpeedMs: number | null = data.value?.raceStats?.results?.[0]?.scoring_speed_corrected_ms ?? null
const bestRaceSpeedKn = bestRaceSpeedMs != null ? (bestRaceSpeedMs * 1.94384).toFixed(2) : null

const challengeRank = computed(() => {
  const results = data.value?.leaderboard?.results ?? []
  return results.find((r: any) => r.user.id === profile?.id) ?? null
})

const skipperRank = computed(() => data.value?.skipperRanking?.results?.[0] ?? null)

const shareCard = useTemplateRef<any>('shareCard')
const showShareDialog = ref(false)

const profileUrl = computed(() =>
  typeof window !== 'undefined' ? `${window.location.origin}/profiles/${profile.slug}-${profile.id}` : ''
)

const shareOnFacebook = () => {
  window.open(
    `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(profileUrl.value)}`,
    '_blank', 'width=600,height=460,noopener'
  )
}

const shareOnX = () => {
  const text = `${profile.name}'s sailing profile on SailingMetrics #sailing #sailingmetrics`
  window.open(
    `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(profileUrl.value)}`,
    '_blank', 'width=600,height=460,noopener'
  )
}

const downloadForInstagram = () => shareCard.value?.download()

const competitionsHeaders = [
  { title: 'Date',        key: 'date',        sortable: false },
  { title: 'Sail number', key: 'sail_number', sortable: false },
  { title: 'Name',        key: 'name',        sortable: false },
]
const itemsPerPage = ref(10)

const seoDescription = `View ${profile.name}'s sailing profile on SailingMetrics — recorded GPS tracks, competition history, and performance data.`
useSeoMeta({
  title: `${profile.name} – Sailor Profile`,
  description: seoDescription,
  ogTitle: `${profile.name} – Sailor Profile`,
  ogDescription: seoDescription,
  ogImage: `https://sailingmetrics.com/og/profiles/${profile.id}`,
  ogType: 'profile',
})
</script>

<template>
  <v-container>

    <!-- Hero -->
    <v-row justify="center" class="mt-2 mb-4">
      <v-col cols="12" sm="10" md="7" lg="5" class="d-flex flex-column align-center text-center ga-3">
        <Avatar :size="96" :name="profile.name" :src="profile.avatar_url" />

        <div>
          <div class="d-flex align-center justify-center ga-2 mb-1">
            <h1 class="text-h4 font-weight-bold">{{ profile.name }}</h1>
            <v-img
              v-if="profile.national_association"
              :src="`/flags/${profile.national_association_details?.iso3166.toLowerCase()}.svg`"
              width="26"
              :max-width="26"
              contain
            />
          </div>

          <div class="d-flex align-center justify-center flex-wrap ga-2">
            <v-chip v-if="profile.sail_number" size="small" prepend-icon="mdi-sail-boat" variant="tonal">
              {{ profile.sail_number }}
            </v-chip>
            <v-chip v-if="profile.sailing_club_details" size="small" prepend-icon="mdi-anchor" variant="tonal">
              {{ profile.sailing_club_details.name }}
            </v-chip>
          </div>
        </div>

        <v-btn
          prepend-icon="mdi-share-variant"
          variant="tonal"
          size="small"
          @click="showShareDialog = true"
        >
          Share
        </v-btn>
      </v-col>
    </v-row>

    <!-- Share dialog -->
    <v-dialog v-model="showShareDialog" max-width="860">
      <v-card>
        <v-card-title class="d-flex align-center pa-4">
          Share this profile
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" size="small" @click="showShareDialog = false" />
        </v-card-title>
        <v-card-text class="pa-4 pt-0">
          <UserShareCard ref="shareCard" :profile="profile" />
          <div class="d-flex align-center flex-wrap ga-2 mt-4">
            <v-btn color="primary" prepend-icon="mdi-download" @click="shareCard?.download()">
              Download
            </v-btn>
            <v-btn
              v-if="shareCard?.canNativeShare"
              variant="tonal"
              prepend-icon="mdi-share-variant"
              @click="shareCard?.nativeShare()"
            >
              Share
            </v-btn>
            <v-btn
              variant="tonal"
              :prepend-icon="shareCard?.copied ? 'mdi-check' : 'mdi-link-variant'"
              @click="shareCard?.copyLink()"
            >
              {{ shareCard?.copied ? 'Copied!' : 'Copy link' }}
            </v-btn>
            <v-divider vertical class="mx-1" />
            <v-btn icon size="small" variant="tonal" title="Share on Facebook" @click="shareOnFacebook">
              <v-icon>mdi-facebook</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="tonal" title="Share on X" @click="shareOnX">
              <v-icon>mdi-twitter</v-icon>
            </v-btn>
            <v-tooltip text="Download the card, then post to Instagram">
              <template #activator="{ props: tip }">
                <v-btn v-bind="tip" icon size="small" variant="tonal" @click="downloadForInstagram">
                  <v-icon>mdi-instagram</v-icon>
                </v-btn>
              </template>
            </v-tooltip>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Lifetime stats -->
    <v-row v-if="profile.stats" justify="center" class="mb-4">
      <v-col cols="auto">
        <div class="d-flex align-center flex-wrap justify-center ga-6">
          <div class="d-flex align-center ga-2">
            <v-icon color="blue" size="20">mdi-map-marker-distance</v-icon>
            <div class="text-center">
              <div class="text-h6 font-weight-bold line-height-1">{{ profile.stats.distance_nm.toLocaleString() }}</div>
              <div class="text-caption text-medium-emphasis">nm sailed</div>
            </div>
          </div>
          <v-divider vertical style="height: 36px" />
          <div class="d-flex align-center ga-2">
            <v-icon color="teal" size="20">mdi-clock-outline</v-icon>
            <div class="text-center">
              <div class="text-h6 font-weight-bold line-height-1">{{ profile.stats.hours }}</div>
              <div class="text-caption text-medium-emphasis">hours on water</div>
            </div>
          </div>
          <v-divider vertical style="height: 36px" />
          <div class="d-flex align-center ga-2">
            <v-icon color="orange" size="20">mdi-speedometer</v-icon>
            <div class="text-center">
              <div class="text-h6 font-weight-bold line-height-1">{{ profile.stats.top_speed_kn }}</div>
              <div class="text-caption text-medium-emphasis">top speed (kn)</div>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Performance cards -->
    <v-row v-if="challengeRank || raceCount > 0" justify="center" class="mb-4">
      <v-col v-if="challengeRank" cols="12" sm="auto">
        <v-card variant="tonal" color="primary" min-width="210">
          <v-card-text class="text-center pa-4">
            <div class="text-caption text-medium-emphasis mb-2">{{ currentYear }} Sailing Challenge</div>
            <div class="d-flex align-center justify-center ga-4">
              <div>
                <div class="text-h4 font-weight-bold">#{{ challengeRank.rank }}</div>
                <div class="text-caption text-medium-emphasis">overall</div>
              </div>
              <v-divider vertical style="height: 40px" />
              <div>
                <div class="text-h5 font-weight-bold">{{ challengeRank.score }}</div>
                <div class="text-caption text-medium-emphasis">score</div>
              </div>
            </div>
            <v-btn
              :to="`/challenges/?year=${currentYear}`"
              variant="text"
              size="small"
              class="mt-2"
              append-icon="mdi-arrow-right"
            >Leaderboard</v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="raceCount > 0" cols="12" sm="auto">
        <v-card variant="tonal" color="blue" min-width="210">
          <v-card-text class="text-center pa-4">
            <div class="text-caption text-medium-emphasis mb-2">Virtual Racing</div>
            <div class="d-flex align-center justify-center ga-4">
              <template v-if="skipperRank">
                <div>
                  <div class="text-h4 font-weight-bold">#{{ skipperRank.rank }}</div>
                  <div class="text-caption text-medium-emphasis">{{ currentYear }} rank</div>
                </div>
                <v-divider vertical style="height: 40px" />
              </template>
              <div>
                <div class="text-h4 font-weight-bold">{{ raceCount }}</div>
                <div class="text-caption text-medium-emphasis">races</div>
              </div>
              <template v-if="bestRaceSpeedKn">
                <v-divider vertical style="height: 40px" />
                <div>
                  <div class="text-h5 font-weight-bold">{{ bestRaceSpeedKn }}</div>
                  <div class="text-caption text-medium-emphasis">best kn (corrected)</div>
                </div>
              </template>
            </div>
            <v-btn
              to="/league/skipper"
              variant="text"
              size="small"
              class="mt-2"
              append-icon="mdi-arrow-right"
            >Leaderboard</v-btn>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Boats & countries -->
    <v-row v-if="profile.stats?.boats?.length || profile.stats?.countries?.length" justify="center" class="mb-4">
      <v-col v-if="profile.stats?.boats?.length" cols="12" sm="6" md="5">
        <v-card class="fill-height">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon start size="20">mdi-sail-boat</v-icon>
            Boats sailed
          </v-card-title>
          <v-card-text class="d-flex flex-wrap ga-2">
            <v-chip v-for="boat in profile.stats.boats" :key="boat.id" size="small" variant="tonal">
              {{ boat.name }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-if="profile.stats?.countries?.length" cols="12" sm="6" md="5">
        <v-card class="fill-height">
          <v-card-title class="text-subtitle-1 font-weight-bold">
            <v-icon start size="20">mdi-earth</v-icon>
            Countries sailed in
          </v-card-title>
          <v-card-text class="d-flex flex-wrap ga-2">
            <v-chip v-for="country in profile.stats.countries" :key="country.id" size="small" variant="tonal">
              <v-img
                :src="`/flags/${country.iso3166.toLowerCase()}.svg`"
                width="18"
                :max-width="18"
                contain
                class="mr-2"
              />
              {{ country.name }}
            </v-chip>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Recent sessions -->
    <ActivityTiles
      :activities="activities"
      heading="Recent sessions"
      :show-all-to="`/sails/?user=${profile.id}`"
      show-all-label="View all"
      :show-user="false"
    />

    <!-- Competition history -->
    <v-row>
      <v-col>
        <h2 class="text-h6 font-weight-bold mb-3">Sailing competitions</h2>
        <v-data-table-server
          v-model:items-per-page="itemsPerPage"
          :headers="competitionsHeaders"
          :items="entries"
          :items-length="totalItems"
          :mobile="smAndDown"
        >
          <template #item.name="{ item }">
            <nuxt-link
              v-if="item.competition?.id"
              :to="{ name: 'competitions-competition___en', params: { competition: `${item.competition.slug}-${item.competition.id}` } }"
            >{{ item.competition.name }}</nuxt-link>
            <span v-else>{{ item.competition?.name }}</span>
          </template>
          <template #item.date="{ item }">
            {{ niceDate(item.competition.start_date) }}
            <span v-show="item.competition.start_date !== item.competition.end_date">
              – {{ niceDate(item.competition.end_date) }}
            </span>
          </template>
        </v-data-table-server>
      </v-col>
    </v-row>

  </v-container>
</template>
